import { generatePath } from 'react-router-dom';
import type { IntakeSubmissionListItem } from '@/api/intake/intakeApi.types';
import type { OwnerPet } from '@/api/patients/patientsApi.types';
import type { PetExercisesDueToday } from '@/api/portalSummary/portalSummaryApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { INTAKE_SUBMISSION_STATUS } from '@/constants/intake';

export const NEXT_STEP_KIND = {
  intakeReturned: 'intakeReturned',
  intakeDraft: 'intakeDraft',
  intakeNotStarted: 'intakeNotStarted',
  exercisesDue: 'exercisesDue',
  unreadMessages: 'unreadMessages',
  consentsNeedReacceptance: 'consentsNeedReacceptance',
} as const;

export type NextStepKind = (typeof NEXT_STEP_KIND)[keyof typeof NEXT_STEP_KIND];

export interface NextStepItem {
  kind: NextStepKind;
  titleKey: string;
  descriptionKey: string;
  actionLabelKey: string;
  actionRoute: string;
  params: Record<string, string | number>;
}

export interface BuildNextStepsInput {
  pets: OwnerPet[];
  intakeSubmissions: IntakeSubmissionListItem[];
  petsWithExercisesDueToday: PetExercisesDueToday[];
  unreadMessageCount: number;
  consentsNeedingReacceptanceCount: number;
}

function buildKeys(kind: string): {
  titleKey: string;
  descriptionKey: string;
  actionLabelKey: string;
} {
  const prefix = 'clients:portal.nextSteps.' + kind;
  return {
    titleKey: prefix + '.title',
    descriptionKey: prefix + '.description',
    actionLabelKey: prefix + '.action',
  };
}

function buildIntakeSubmissionItem(
  kind: typeof NEXT_STEP_KIND.intakeReturned | typeof NEXT_STEP_KIND.intakeDraft,
  submission: IntakeSubmissionListItem
): NextStepItem {
  return {
    kind,
    ...buildKeys(kind),
    actionRoute: generatePath(APPLICATION_ROUTES.clientPortalIntakeSubmission, {
      submissionId: submission.id,
    }),
    params: { petName: submission.petName === null ? '' : submission.petName },
  };
}

function buildIntakeNotStartedItem(pet: OwnerPet): NextStepItem {
  return {
    kind: NEXT_STEP_KIND.intakeNotStarted,
    ...buildKeys(NEXT_STEP_KIND.intakeNotStarted),
    actionRoute: APPLICATION_ROUTES.clientPortalIntake,
    params: { petName: pet.name },
  };
}

function buildIntakeSteps(
  pets: OwnerPet[],
  intakeSubmissions: IntakeSubmissionListItem[]
): NextStepItem[] {
  const patientIdsWithSubmission = new Set(
    intakeSubmissions
      .filter(function hasLinkedPatient(submission) {
        return submission.patientId !== null;
      })
      .map(function toPatientId(submission) {
        return submission.patientId as string;
      })
  );

  const returnedSteps = intakeSubmissions
    .filter(function isReturned(submission) {
      return submission.status === INTAKE_SUBMISSION_STATUS.returned;
    })
    .map(function toReturnedItem(submission) {
      return buildIntakeSubmissionItem(NEXT_STEP_KIND.intakeReturned, submission);
    });

  const draftSteps = intakeSubmissions
    .filter(function isDraft(submission) {
      return submission.status === INTAKE_SUBMISSION_STATUS.draft;
    })
    .map(function toDraftItem(submission) {
      return buildIntakeSubmissionItem(NEXT_STEP_KIND.intakeDraft, submission);
    });

  const notStartedSteps = pets
    .filter(function hasNoSubmissionYet(pet) {
      return patientIdsWithSubmission.has(pet.id) === false;
    })
    .map(function toNotStartedItem(pet) {
      return buildIntakeNotStartedItem(pet);
    });

  return [...returnedSteps, ...draftSteps, ...notStartedSteps];
}

function buildExercisesDueSteps(petsWithExercisesDueToday: PetExercisesDueToday[]): NextStepItem[] {
  if (petsWithExercisesDueToday.length === 0) {
    return [];
  }
  const totalDueExerciseCount = petsWithExercisesDueToday.reduce(function addDueCount(
    total,
    summary
  ) {
    return total + summary.dueExerciseCount;
  }, 0);
  const singlePetSummary =
    petsWithExercisesDueToday.length === 1 ? petsWithExercisesDueToday[0] : undefined;
  const actionRoute =
    singlePetSummary === undefined
      ? APPLICATION_ROUTES.clientPortalPets
      : generatePath(APPLICATION_ROUTES.clientPortalPet, { patientId: singlePetSummary.patientId });

  return [
    {
      kind: NEXT_STEP_KIND.exercisesDue,
      ...buildKeys(NEXT_STEP_KIND.exercisesDue),
      actionRoute,
      params: {
        count: totalDueExerciseCount,
        petName: singlePetSummary === undefined ? '' : singlePetSummary.patientName,
      },
    },
  ];
}

function buildUnreadMessagesSteps(unreadMessageCount: number): NextStepItem[] {
  if (unreadMessageCount <= 0) {
    return [];
  }
  return [
    {
      kind: NEXT_STEP_KIND.unreadMessages,
      ...buildKeys(NEXT_STEP_KIND.unreadMessages),
      actionRoute: APPLICATION_ROUTES.clientPortalChat,
      params: { count: unreadMessageCount },
    },
  ];
}

function buildConsentsReacceptanceSteps(consentsNeedingReacceptanceCount: number): NextStepItem[] {
  if (consentsNeedingReacceptanceCount <= 0) {
    return [];
  }
  return [
    {
      kind: NEXT_STEP_KIND.consentsNeedReacceptance,
      ...buildKeys(NEXT_STEP_KIND.consentsNeedReacceptance),
      actionRoute: APPLICATION_ROUTES.clientPortalForms,
      params: { count: consentsNeedingReacceptanceCount },
    },
  ];
}

export function buildNextSteps(input: BuildNextStepsInput): NextStepItem[] {
  return [
    ...buildIntakeSteps(input.pets, input.intakeSubmissions),
    ...buildExercisesDueSteps(input.petsWithExercisesDueToday),
    ...buildUnreadMessagesSteps(input.unreadMessageCount),
    ...buildConsentsReacceptanceSteps(input.consentsNeedingReacceptanceCount),
  ];
}
